<script setup>
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { models, devModels } from '@/lib/gen';
import ModelIcon from '@/components/icons/ModelIcon.vue';
import { computed, ref } from 'vue';
import { ChevronDown, ChevronUp } from 'lucide-vue-next';
import ModelItem from '@/components/model/ModelItem.vue';
import { cn } from '@/lib/utils';
import { useRoute } from 'vue-router';
import Tooltip2 from '@/components/Tooltip2.vue';

const props = defineProps({
  modelValue: String,
  class: String,
  open: Boolean,
});

const emit = defineEmits(['update:model-value', 'update:open', 'hover-model']);

const route = useRoute();

const showMoreModels = ref(false);

const modelValue = computed({
  get: () => {
    return props.modelValue;
  },
  set: (v) => emit('update:model-value', v),
});

const useModels = computed(() => {
  const isDev = !!route.query.beta;
  if (!isDev) return models;
  return [...devModels, ...models];
});

const selectedModel = computed(() => {
  return useModels.value.find((model) => model.value === modelValue.value);
});

// 推荐的前4个模型
const recommendedModels = computed(() => {
  return useModels.value.slice(0, 5);
});

// 其他模型
const otherModels = computed(() => {
  return useModels.value.slice(5);
});

const toggleMoreModels = () => {
  showMoreModels.value = !showMoreModels.value;
};

const selectModel = (model) => {
  modelValue.value = model.value;
};

const handleModelHover = (model) => {
  emit('hover-model', model);
};

const handleModelLeave = () => {
  emit('hover-model', null);
};

const isOpen = computed({
  get: () => props.open,
  set: (v) => emit('update:open', v),
});
</script>

<template>
  <Tooltip2 :text="$t('modelSelector.selectModel')">
    <Popover v-model:open="isOpen">
      <PopoverTrigger as-child>
        <button
          :class="
            cn(
              'group w-min h-8 rounded-full sm:h-9 bg-white text-black/60 border border-black/5 hover:bg-black/5 hover:border-black/20 data-[state=open]:bg-black/5 data-[state=open]:border-black/20 focus:ring-0 focus:outline-none flex items-center gap-1 px-2 transition-all duration-200',
              props.class
            )
          "
        >
          <div v-if="selectedModel" class="flex items-center gap-0 min-w-0 flex-1 mr-1">
            <div class="shrink-0 size-6 flex items-center justify-center">
              <ModelIcon :type="selectedModel.icon" class="size-4 text-blue-600" />
            </div>
            <span
              class="text-sm text-black/60 group-hover:text-black/90 transition-colors truncate"
              >{{ selectedModel.label }}</span
            >
          </div>
          <div v-else class="text-xs text-black/60 font-medium whitespace-nowrap">
            {{ $t('modelSelector.selectModel') }}
          </div>
          <ChevronDown
            class="size-4 transition-transform duration-200 text-black/30 group-hover:text-black/90"
          />
        </button>
      </PopoverTrigger>

      <PopoverContent
        class="w-[480px] p-0 rounded-[20px] backdrop-blur-[60px]"
        style="
          background-color: rgba(255, 255, 255, 0.85);
          border: 1px solid rgba(0, 0, 0, 0.07);
          box-shadow:
            0 20px 25px -5px rgba(0, 0, 0, 0.1),
            0 8px 10px -6px rgba(0, 0, 0, 0.1);
        "
      >
        <!-- Header Section -->
        <div class="px-4 py-3 border-b border-black/5">
          <div class="flex items-center justify-between">
            <div class="text-sm font-normal text-black/60">
              {{
                showMoreModels
                  ? $t('modelSelector.allModels')
                  : $t('modelSelector.recommendedModels')
              }}
            </div>
          </div>
        </div>

        <div
          class="max-h-80 overflow-y-auto scrollbar-thin scrollbar-thumb-black/10 scrollbar-track-transparent hover:scrollbar-thumb-black/20 scrollbar-thumb-rounded-full"
        >
          <!-- Recommended Models Section -->
          <div class="p-2">
            <ModelItem
              v-for="(model, index) in recommendedModels"
              :key="model.value"
              :model="model"
              :modelValue="modelValue"
              @click="selectModel(model)"
              @mouseenter="handleModelHover(model)"
              @mouseleave="handleModelLeave"
              class="relative"
            >
              <!-- Top Badge for first model -->
              <div
                v-if="index === 0 && !showMoreModels"
                class="absolute -top-1 -right-1 bg-gradient-to-r from-blue-500 to-indigo-500 text-white text-xs px-2 py-0.5 rounded-full font-medium shadow-sm z-10"
              >
                {{ $t('modelSelector.mostPopular') }}
              </div>
              <!-- Ranking badge for top 4 -->
              <div
                v-if="!showMoreModels"
                class="absolute top-2 left-2 bg-white/90 backdrop-blur-sm text-black/60 text-xs w-6 h-6 rounded-full flex items-center justify-center font-bold border border-black/20 shadow-sm z-10"
              >
                {{ index + 1 }}
              </div>
            </ModelItem>
          </div>

          <!-- More Models Button -->
          <div v-if="otherModels.length > 0 && !showMoreModels" class="p-2">
            <button
              @click="toggleMoreModels"
              class="group w-full px-4 py-3 text-center cursor-pointer hover:bg-black/5 rounded-xl transition-all duration-300 ease-out border border-dashed border-black/10 hover:border-black/20"
            >
              <div class="text-sm font-medium text-black/50 group-hover:text-black/80">
                {{ $t('modelSelector.exploreMore') }}
              </div>
            </button>
          </div>

          <!-- All Models View -->
          <div v-if="showMoreModels">
            <!-- Other Models Section -->
            <div v-if="otherModels.length > 0" class="px-2 pb-2">
              <div class="px-4 py-2 mt-2">
                <div class="text-sm font-normal text-black/60">
                  {{ $t('modelSelector.otherModels') }}
                </div>
              </div>

              <ModelItem
                v-for="model in otherModels"
                :key="model.value"
                :model="model"
                :modelValue="modelValue"
                @click="selectModel(model)"
                @mouseenter="handleModelHover(model)"
                @mouseleave="handleModelLeave"
              />
            </div>

            <!-- Collapse Button -->
            <div class="p-2 border-t border-black/10">
              <button
                @click="toggleMoreModels"
                class="group w-full px-4 py-3 text-center cursor-pointer hover:bg-black/5 rounded-xl transition-all duration-300 ease-out"
              >
                <div
                  class="flex items-center justify-center gap-2 text-sm text-black/50 group-hover:text-black/80 font-medium"
                >
                  <span>{{ $t('modelSelector.showLess') }}</span>
                  <ChevronUp
                    class="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5"
                  />
                </div>
              </button>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  </Tooltip2>
</template>
